import { TimelineMax, TweenLite } from "gsap";
import { Translation } from "@nolimitcity/slot-game/bin/core/translation/Translation";
import { TextStyleOptions } from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import { ResizeHelper, IDimConfig, LayoutDimData } from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import { Point } from "pixi.js";
import { NineSliceButton } from "@nolimitcity/slot-game/bin/core/component/NineSliceButton";
import { IBackPlateMeshConfig } from "@nolimitcity/slot-game/bin/core/displayobject/BasePlateMesh";
import { StateHandler } from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import { GameResources } from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import { BaseView } from "@nolimitcity/slot-game/bin/core/base/BaseView";
import { TranslationConfig } from "../../../../translation/TranslationConfig";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import { TextStyleConfig } from "../../../../translation/TextStyleConfig";
import { SoundManager } from "../../../../resource/sound/SoundManager";
import { SoundConfig } from "../../../../SoundConfig";
import { MathHelper } from "@nolimitcity/slot-game/bin/core/utils/MathHelper";


export class SplashButtonType extends StateHandler {
    public static readonly CONTINUE:string = "continue";
    public static readonly RIGHT:string = "right";
    public static readonly LEFT:string = "left";
}

export class SplashButton extends BaseView {

    protected readonly continueDim: IDimConfig = {
        dimOrientation: {
            landscape: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 580),
                layout: {
                    left: new Point(360, 580),
                    right: new Point(360, 580)
                }
            },
            portrait: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 650)
            }
        },
        WIDTH: 240,
        HEIGHT: 64,
    };

    protected readonly sideDim: IDimConfig = {
        dimOrientation: {
            landscape: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 360),
                layout: {
                    left: new Point(360, 360),
                    right: new Point(360, 360)
                }
            },
            portrait: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 360)
            }
        },
        WIDTH: 240,
        HEIGHT: 64,
    };

    protected readonly DEFAULT_BTN_BACKGROUND: IBackPlateMeshConfig = {
        cornerRadius: 12,
        lineThickness: 2,
        lineColor: 0xffffff,
        lineAlpha: 1,
        backgroundColor: 0x000000,
        backgroundAlpha: 0.9
    };

    protected readonly DEFAULT_BUTTON_TEXT: string = "CONTINUE";

    protected buttonType:SplashButtonType = new SplashButtonType(SplashButtonType.CONTINUE);
    protected callBack: Function | undefined = () => {};
    protected _altCB: Function | undefined = () => {};
    protected _button: NineSliceButton | PIXI.Sprite;

    protected animationTimeline:TimelineMax;
    protected btnTimeline:TimelineMax;

    protected get continueBtn():NineSliceButton | undefined{
        return this.buttonType.equal(SplashButtonType.CONTINUE) ? this._button as NineSliceButton : undefined;
    }

    protected get sideBtn():PIXI.Sprite | undefined{
        return !this.buttonType.equal(SplashButtonType.CONTINUE) ? this._button as PIXI.Sprite : undefined;
    }

    constructor(cb: Function, type:string = SplashButtonType.CONTINUE) {
        super();
        this.buttonType.set(type);
        this.visible = false;
        this.callBack = cb;
    }

    protected initAnimations():void{
        this.createButtons();
    }

    protected onClick(): void {
        SoundManager.playSoundEffect(SoundConfig.instance.CONTINUE_CLICK);
        if(this._altCB) this._altCB();
        else if (this.callBack) this.callBack();
    }

    protected sideClick():void{
        this.animationTimeline = Helper.timeLineKiller(this.animationTimeline, new TimelineMax())
        this.animationTimeline.to(this.sideBtn!.scale,0.2,{x:1.1, y:1.1});
        this.animationTimeline.to(this.sideBtn!.scale,0.2,{x:1,y:1});
        this.animationTimeline.play();
    }

    public showButton(cb?:Function): TimelineMax {
        this._altCB = cb;

        this.btnTimeline = Helper.timeLineKiller(this.btnTimeline, new TimelineMax({
            onStart: () => {
                this.visible = true;
                this.setInteractive(true);
            },
        }));
        this.btnTimeline.fromTo(this, 0.2, {alpha:0}, {alpha:1});
        this.btnTimeline.to(this, Number.MAX_SAFE_INTEGER, {});
        this.btnTimeline.play();
        return this.btnTimeline;
    }

    public changeText(val:string):void{
        (<NineSliceButton>this._button).setButtonText(val);
    }

    public hideButton(duration:number = 0.35):TimelineMax{
        this.btnTimeline = Helper.timeLineKiller(this.btnTimeline, new TimelineMax({
            onComplete: () => {
                this.visible = false;
                this.setInteractive(false);
            }
        }));
        this.btnTimeline.to(this, duration, {alpha:0});
        this.btnTimeline.play();
        return this.btnTimeline;
    }

    public setInteractive(value:boolean):void{
        if(this.continueBtn) this.continueBtn.enabled = value;
        else if(this.sideBtn){
            this.sideBtn.interactive = value;
            this.sideBtn.buttonMode = value;
        }
    }

    protected createButtons(): void {
        if(this.buttonType.equal(SplashButtonType.CONTINUE))
            this._button = this.createContinue();
        else{
            this._button = this.createSide();
        }
    }

    protected createSide(): PIXI.Sprite {
        const button = new PIXI.Sprite(GameResources.getTextures("in_game_arrow")[0]);
        button.anchor.set(0.5,0.5);
        button.scale.set(this.buttonType.equal(SplashButtonType.RIGHT) ? 0.5 : -0.5, 0.5);
        button.interactive = false;
        button.buttonMode = false;
        button.on('pointerdown', () => this.onClick());
        this.addChild(button);
        return button;
    }

    protected createContinue(): NineSliceButton {
        const text: string = Translation.translate(TranslationConfig.CONTINUE);
        const textStyle: TextStyleOptions = this.getScaledButtonTextStyle();
        const button = new NineSliceButton(() => this.onClick(), ` ${text} `, textStyle, this.DEFAULT_BTN_BACKGROUND);
        button.setSize(this.continueDim.WIDTH, this.continueDim.HEIGHT, 1);
        button.enabled = false;
        this.addChild(button);
        return button;
    }

    protected getScaledButtonTextStyle(): TextStyleOptions {
        const style: TextStyleOptions = TextStyleConfig.DEFAULT_BUTTON_TEXT_STYLE;
        style.fontSize = (<number>style.fontSize);
        return style;
    }

    public onResize(): void {
        const dimConf = this.buttonType.equal(SplashButtonType.CONTINUE) ? this.continueDim : this.sideDim;
        const layoutdata: LayoutDimData = ResizeHelper.calcDim(dimConf);

        this.position = layoutdata.position;
        this.scale = layoutdata.scale;

        if(ResizeHelper.isIPadd() && !ResizeHelper.IsLandscape()){
            this.position.y -= 35;
            this.scale = MathHelper.pointMul(<PIXI.Point>this.scale,0.85);
        }

        if(!this.buttonType.equal(SplashButtonType.CONTINUE))
            this.position.x += this.buttonType.equal(SplashButtonType.RIGHT) ? 300 : -300;
    }
}
